import { useState } from 'react'
import { toast } from 'sonner'

import { useChannels } from '@/api/endpoints/channels'
import {
  useRetentionPurge,
  useRetentionPurgePreview,
  useRunCleanupAll,
} from '@/api/endpoints/scheduler'
import { useSettings, useUpdateSettings } from '@/api/endpoints/settings'
import { Button } from '@/components/ui/Button'
import { DialogClose, DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { errorMessage } from '@/lib/errors'
import styles from './DiskRetentionSection.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

const ALL_CHANNELS = '__all__'
const TTL_MIN = 0
const TTL_MAX = 90

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${String(bytes)} B`
}

function todayIso(): string {
  const now = new Date()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `${String(now.getFullYear())}-${mm}-${dd}`
}

/**
 * Bloco "Disco e retenção" da /settings: TTL do cache de clipes, trigger
 * manual do ciclo de limpeza diário e a purga (destrutiva, com preview
 * obrigatório) de vídeos já publicados.
 */
export function DiskRetentionSection() {
  return (
    <div className={styles.wrap}>
      <CacheTtlBlock />
      <CleanupBlock />
      <PurgeBlock />
    </div>
  )
}

/** TTL da eviction do cache de clipes — persiste em ``global_settings``. */
function CacheTtlBlock() {
  const settingsQuery = useSettings()
  const mutation = useUpdateSettings()
  const saved = settingsQuery.data?.media_cache_ttl_days ?? null
  const [value, setValue] = useState<string | null>(null) // null = não editado
  const shown = value ?? (saved === null ? '' : String(saved))
  const dirty = value !== null && value !== (saved === null ? '' : String(saved))
  const numeric = shown === '' ? null : Number(shown)
  const invalid = numeric !== null && (!Number.isInteger(numeric) || numeric < TTL_MIN || numeric > TTL_MAX)

  const save = () => {
    mutation.mutate(
      { media_cache_ttl_days: numeric },
      {
        onSuccess: () => {
          setValue(null)
          toast.success('TTL do cache de clipes salvo')
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha ao salvar TTL'))
        },
      },
    )
  }

  return (
    <div className={styles.group}>
      <h4 className={styles.groupTitle}>Cache de clipes</h4>
      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="settings-media-cache-ttl">
            TTL da eviction (dias)
          </label>
          <Input
            id="settings-media-cache-ttl"
            type="number"
            min={TTL_MIN}
            max={TTL_MAX}
            placeholder="Padrão (3 dias)"
            value={shown}
            disabled={settingsQuery.isLoading}
            onChange={(e) => {
              setValue(e.target.value)
            }}
          />
          <span className={styles.hint}>
            Clipes de stock sem uso há mais de N dias são apagados no cleanup diário (03:00).
            0 = desligado · em branco = padrão (3 dias).
          </span>
          {invalid ? (
            <span className={styles.error}>Valor deve ser um inteiro entre {TTL_MIN} e {TTL_MAX}.</span>
          ) : null}
        </div>
        <div className={styles.fieldActions}>
          <Button size="sm" disabled={!dirty || invalid || mutation.isPending} onClick={save}>
            {mutation.isPending ? 'Salvando…' : 'Salvar TTL'}
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Trigger manual do ciclo de limpeza (o mesmo do cron das 03:00). */
function CleanupBlock() {
  const runCleanup = useRunCleanupAll()

  return (
    <div className={styles.group}>
      <h4 className={styles.groupTitle}>Limpeza diária</h4>
      <div className={styles.inlineAction}>
        <p className={styles.hint}>
          Expira vídeos <strong>não publicados</strong> fora da janela de upload de cada canal
          (backlog, padrão 7 dias) e aplica a eviction do cache de clipes. Roda todo dia às
          03:00; o botão dispara o mesmo ciclo agora.
        </p>
        <Button
          size="sm"
          variant="secondary"
          disabled={runCleanup.isPending}
          onClick={() => {
            runCleanup.mutate(undefined, {
              onSuccess: () => {
                toast.success('Ciclo de limpeza iniciado')
              },
              onError: (err) => {
                toast.error(errorMessage(err, 'Falha ao iniciar limpeza (cooldown de 60s?)'))
              },
            })
          }}
        >
          {runCleanup.isPending ? 'Iniciando…' : 'Rodar limpeza agora'}
        </Button>
      </div>
    </div>
  )
}

/** Purga destrutiva de publicados: data de corte + canal + dialog com preview. */
function PurgeBlock() {
  const { data: channels = [] } = useChannels()
  const purge = useRetentionPurge()
  const [before, setBefore] = useState('')
  const [channel, setChannel] = useState(ALL_CHANNELS)
  const [dialogOpen, setDialogOpen] = useState(false)

  const channelSlug = channel === ALL_CHANNELS ? null : channel
  const channelLabel =
    channelSlug === null
      ? 'todos os canais'
      : (channels.find((c) => c.slug === channelSlug)?.name ?? channelSlug)
  const preview = useRetentionPurgePreview(before, channelSlug, dialogOpen && before !== '')

  const confirm = () => {
    purge.mutate(
      { before, channel_slug: channelSlug },
      {
        onSuccess: (report) => {
          setDialogOpen(false)
          const freed = formatBytes(report.bytes_freed)
          if (report.failed > 0) {
            toast.warning(
              `Purga parcial: ${String(report.deleted)} vídeo(s) apagados (${freed}), ${String(report.failed)} falhou(aram)`,
            )
          } else {
            toast.success(`${String(report.deleted)} vídeo(s) apagados do disco (${freed})`)
          }
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Falha na purga'))
        },
      },
    )
  }

  return (
    <div className={styles.group}>
      <h4 className={styles.groupTitle}>Purga de vídeos publicados</h4>
      <p className={styles.hint}>
        Vídeos já publicados são preservados no disco como histórico. Aqui você apaga os
        arquivos locais dos publicados <strong>antes</strong> de uma data — os registros de
        upload (YouTube) e o histórico no banco são mantidos.
      </p>
      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="settings-purge-before">
            Apagar publicados antes de
          </label>
          <Input
            id="settings-purge-before"
            type="date"
            max={todayIso()}
            value={before}
            onChange={(e) => {
              setBefore(e.target.value)
            }}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Canal</span>
          <SelectRoot value={channel} onValueChange={setChannel}>
            <SelectTrigger>
              <SelectValue placeholder="Todos os canais" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CHANNELS}>Todos os canais</SelectItem>
              {channels.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          variant="secondary"
          disabled={before === ''}
          onClick={() => {
            setDialogOpen(true)
          }}
        >
          Purgar vídeos antigos…
        </Button>
      </div>

      <DialogRoot
        open={dialogOpen}
        onOpenChange={(next) => {
          if (!next) setDialogOpen(false)
        }}
      >
        <DialogContent
          title="Purgar vídeos publicados"
          description={`Apaga do disco os vídeos publicados antes de ${before} — ${channelLabel}. Registros de upload e histórico são preservados.`}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="secondary" disabled={purge.isPending}>
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                variant="primary"
                disabled={!preview.data || preview.data.videos === 0 || purge.isPending}
                onClick={confirm}
              >
                {purge.isPending ? 'Purgando…' : 'Apagar do disco'}
              </Button>
            </>
          }
        >
          {preview.isLoading ? <p className={styles.hint}>Calculando impacto…</p> : null}
          {preview.isError ? (
            <QueryErrorNotice
              compact
              error={preview.error}
              label="o impacto da purga"
              onRetry={() => {
                void preview.refetch()
              }}
            />
          ) : null}
          {preview.data ? (
            preview.data.videos === 0 ? (
              <p className={styles.hint}>
                Nenhum vídeo publicado antes de {before} com arquivos no disco. Nada a apagar.
              </p>
            ) : (
              <div className={styles.previewBox}>
                <p className={styles.previewHeadline}>
                  <strong>{preview.data.videos} vídeo(s)</strong> ·{' '}
                  <strong>{formatBytes(preview.data.bytes_total)}</strong> serão apagados do disco.
                </p>
                <ul className={styles.previewList}>
                  {Object.entries(preview.data.per_channel).map(([slug, agg]) => (
                    <li key={slug}>
                      {slug}: {agg.videos} vídeo(s) · {formatBytes(agg.bytes)}
                    </li>
                  ))}
                </ul>
              </div>
            )
          ) : null}
        </DialogContent>
      </DialogRoot>
    </div>
  )
}
